// src/components/admin/AdminClientDetailPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Container, Typography, Box, Paper, Button, Chip, Divider, Alert,
  Grid, Avatar, List, ListItem,
  ListItemIcon, ListItemText, Skeleton, LinearProgress
} from '@mui/material';
import {
  ArrowBack, Edit, Delete, Business, Email, Phone, LocationOn,
  AccessTime, CheckCircle, Cancel, Visibility,
  Chat, Analytics, Speed, Token, Memory, DataUsage,
  Settings, Security, Link
} from '@mui/icons-material';
import { adminApi } from '../../api/adminApi';
import StatCard from '../common/StatCard';
import PageHeader from '../common/PageHeader';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -20 }
};

const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.6
};

// Add CSS animations
const globalStyles = `
  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
`;

export default function AdminClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchClientData = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const clientData = await adminApi.getClientDetail(id);
      setClient(clientData);
      
      try {
        const statsData = await adminApi.getClientStats(id);
        setStats(statsData);
      } catch (statsError) {
        console.log('Stats endpoint not available, using fallback data');
        setStats({
          total_conversations: 0,
          active_conversations: 0,
          total_messages: 0,
          avg_response_time: 0,
          success_rate: 0
        });
      }
    } catch (err) {
      console.error('Error fetching client data:', err);
      setError(err.message || 'فشل في تحميل بيانات العميل. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchClientData();
  }, [fetchClientData]);

  const handleEdit = () => {
    navigate(`/admin/clients/edit/${id}`);
  };

  const handleDelete = async () => {
    if (window.confirm('هل أنت متأكد من حذف هذا العميل؟')) {
      try {
        await adminApi.deleteClient(id);
        navigate('/admin/clients');
      } catch (err) {
        console.error('Error deleting client:', err);
        setError(err.message || 'فشل في حذف العميل');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="text" width="60%" height={32} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin/clients')}
        >
          العودة لقائمة العملاء
        </Button>
      </Container>
    );
  }

  if (!client) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h5" color="text.secondary" textAlign="center">
          العميل غير موجود
        </Typography>
      </Container>
    );
  }

  return (
    <>
      <style>{globalStyles}</style>
      <motion.div
        initial="initial"
        animate="in"
        exit="out"
        variants={pageVariants}
        transition={pageTransition}
      >
        <Container maxWidth="lg">
        <PageHeader
          title="تفاصيل العميل"
          subtitle="عرض معلومات العميل والإحصائيات"
          variant="gradient"
          onRefresh={fetchClientData}
          refreshing={loading}
          actions={
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/admin/clients')}
              sx={{ 
                borderRadius: 3,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                px: { xs: 2, sm: 3 },
                py: { xs: 1, sm: 1.5 },
                borderColor: '#3b82f6',
                color: '#3b82f6',
                '&:hover': {
                  bgcolor: '#eff6ff',
                  borderColor: '#2563eb'
                }
              }}
            >
              العودة
            </Button>
          }
        />

        {/* Client Info Card - Responsive */}
        <Paper sx={{
          p: { xs: 3, sm: 4 },
          mb: { xs: 3, sm: 4, md: 5 },
          borderRadius: 4,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
          border: '1px solid #e2e8f0',
          background: '#ffffff',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 25px rgba(0,0,0,0.15), 0 4px 6px rgba(0,0,0,0.1)'
          }
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            justifyContent: 'space-between', 
            mb: { xs: 2, sm: 3 },
            gap: { xs: 2, sm: 0 }
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 2, sm: 3 },
              flexDirection: { xs: 'column', sm: 'row' },
              textAlign: { xs: 'center', sm: 'left' }
            }}>
              <Avatar
                sx={{
                  width: { xs: 60, sm: 70, md: 80 },
                  height: { xs: 60, sm: 70, md: 80 },
                  bgcolor: 'primary.main',
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
                }}
              >
                <Business />
              </Avatar>
              <Box>
                <Typography 
                  variant="h5" 
                  fontWeight="700" 
                  gutterBottom
                  sx={{ 
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.5rem' },
                    lineHeight: { xs: 1.2, sm: 1.3 },
                    color: '#1e293b'
                  }}
                >
                  {client.company_name}
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    color: '#64748b'
                  }}
                  gutterBottom
                >
                  معرف العميل: {client.id}
                </Typography>
                <Chip
                  label={client.is_active ? 'البوت يعمل' : 'البوت متوقف'}
                  icon={client.is_active ? <CheckCircle /> : <Cancel />}
                  size="small"
                  sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    bgcolor: client.is_active ? '#dcfce7' : '#fee2e2',
                    color: client.is_active ? '#166534' : '#991b1b',
                    border: `1px solid ${client.is_active ? '#bbf7d0' : '#fecaca'}`,
                    fontWeight: 600
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'center', sm: 'flex-end' }
            }}>
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={handleEdit}
                sx={{
                  borderRadius: 3,
                  bgcolor: '#3b82f6',
                  '&:hover': {
                    bgcolor: '#2563eb',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)'
                  },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.5 },
                  transition: 'all 0.3s ease'
                }}
              >
                تعديل
              </Button>
              <Button
                variant="outlined"
                startIcon={<Delete />}
                onClick={handleDelete}
                sx={{ 
                  borderRadius: 3,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.5 },
                  borderColor: '#ef4444',
                  color: '#ef4444',
                  '&:hover': {
                    bgcolor: '#fef2f2',
                    borderColor: '#dc2626',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                حذف
              </Button>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} md={6}>
              <List sx={{ p: { xs: 0, sm: 1 } }}>
                <ListItem sx={{ 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 0 }
                }}>
                  <ListItemIcon sx={{ minWidth: { xs: 'auto', sm: 40 } }}>
                    <Email sx={{ color: '#3b82f6', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600,
                        color: '#1e293b'
                      }}>
                        البريد الإلكتروني
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        wordBreak: 'break-word',
                        color: '#64748b'
                      }}>
                        {client.email || 'غير محدد'}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 0 }
                }}>
                  <ListItemIcon sx={{ minWidth: { xs: 'auto', sm: 40 } }}>
                    <Phone sx={{ color: '#3b82f6', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600
                      }}>
                        رقم الهاتف
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}>
                        {client.phone_number || 'غير محدد'}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 0 }
                }}>
                  <ListItemIcon sx={{ minWidth: { xs: 'auto', sm: 40 } }}>
                    <LocationOn sx={{ color: '#3b82f6', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600,
                        color: '#1e293b'
                      }}>
                        العنوان
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        wordBreak: 'break-word',
                        color: '#64748b'
                      }}>
                        {client.address || 'غير محدد'}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <List sx={{ p: { xs: 0, sm: 1 } }}>
                <ListItem sx={{ 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 0 }
                }}>
                  <ListItemIcon sx={{ minWidth: { xs: 'auto', sm: 40 } }}>
                    <AccessTime sx={{ color: '#3b82f6', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600,
                        color: '#1e293b'
                      }}>
                        تاريخ التسجيل
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        color: '#64748b'
                      }}>
                        {formatDate(client.created_at)}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 0 }
                }}>
                  <ListItemIcon sx={{ minWidth: { xs: 'auto', sm: 40 } }}>
                    <AccessTime sx={{ color: '#3b82f6', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600,
                        color: '#1e293b'
                      }}>
                        آخر تحديث
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        color: '#64748b'
                      }}>
                        {formatDate(client.updated_at)}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 0 }
                }}>
                  <ListItemIcon sx={{ minWidth: { xs: 'auto', sm: 40 } }}>
                    <Business sx={{ color: '#3b82f6', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600,
                        color: '#1e293b'
                      }}>
                        نوع الحساب
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        color: '#64748b'
                      }}>
                        {client.is_admin ? 'مدير' : 'عميل'}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Paper>

        {/* Gupshup Settings Section - Responsive */}
        <Paper sx={{ 
          p: { xs: 3, sm: 4 }, 
          mb: { xs: 3, sm: 4, md: 5 }, 
          borderRadius: 4, 
          bgcolor: '#f8fafc',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
          border: '1px solid #e2e8f0',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 25px rgba(0,0,0,0.15), 0 4px 6px rgba(0,0,0,0.1)'
          }
        }}>
          <Typography 
            variant="h6" 
            fontWeight="600" 
            sx={{ 
              mb: { xs: 2, sm: 3 }, 
              fontSize: { xs: '1.125rem', sm: '1.25rem' },
              color: '#1e293b'
            }}
          >
            إعدادات Gupshup
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} md={6}>
              <List sx={{ p: { xs: 0, sm: 1 } }}>
                <ListItem sx={{ 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 0 }
                }}>
                  <ListItemIcon sx={{ minWidth: { xs: 'auto', sm: 40 } }}>
                    <Phone sx={{ color: '#3b82f6', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600,
                        color: '#1e293b'
                      }}>
                        رقم هاتف Gupshup
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        wordBreak: 'break-word',
                        color: '#64748b'
                      }}>
                        {client.gupshup_phone_number || 'غير محدد'}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 0 }
                }}>
                  <ListItemIcon sx={{ minWidth: { xs: 'auto', sm: 40 } }}>
                    <Settings sx={{ color: '#3b82f6', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600,
                        color: '#1e293b'
                      }}>
                        اسم تطبيق Gupshup
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        wordBreak: 'break-word',
                        color: '#64748b'
                      }}>
                        {client.gupshup_app_name || 'غير محدد'}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <List sx={{ p: { xs: 0, sm: 1 } }}>
                <ListItem sx={{ 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 0 }
                }}>
                  <ListItemIcon sx={{ minWidth: { xs: 'auto', sm: 40 } }}>
                    <Security color="primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600
                      }}>
                        مفتاح API
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        fontFamily: 'monospace'
                      }}>
                        {client.gupshup_api_key ? '***' + client.gupshup_api_key.slice(-4) : 'غير محدد'}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 0 }
                }}>
                  <ListItemIcon sx={{ minWidth: { xs: 'auto', sm: 40 } }}>
                    <Link color="primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600
                      }}>
                        رابط API
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        wordBreak: 'break-all'
                      }}>
                        {client.gupshup_api_url || 'غير محدد'}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Paper>

        {/* Statistics - Responsive */}
        <Box sx={{ 
          mb: { xs: 2, sm: 3 },
          textAlign: 'center',
          position: 'relative'
        }}>
          <Typography 
            variant="h5" 
            fontWeight="700" 
            sx={{ 
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.5rem' },
              color: 'primary.main'
            }}
          >
            إحصائيات العميل
          </Typography>
          <Box sx={{
            width: '60px',
            height: '3px',
            bgcolor: 'primary.main',
            margin: '8px auto 0',
            borderRadius: '2px'
          }} />
        </Box>

        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="إجمالي المحادثات"
              value={stats?.total_conversations || 0}
              icon={<Chat />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="المحادثات النشطة"
              value={stats?.active_conversations || 0}
              icon={<Visibility />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="إجمالي الرسائل"
              value={stats?.total_messages || 0}
              icon={<Analytics />}
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="معدل الاستجابة (دقيقة)"
              value={stats?.avg_response_time || 0}
              icon={<Speed />}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="إجمالي التوكنز"
              value={client?.total_tokens_consumed ? client.total_tokens_consumed.toLocaleString() : '0'}
              icon={<Token />}
              color="secondary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="توكنز المدخلات"
              value={client?.total_prompt_tokens ? client.total_prompt_tokens.toLocaleString() : '0'}
              icon={<Memory />}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="توكنز المخرجات"
              value={client?.total_completion_tokens ? client.total_completion_tokens.toLocaleString() : '0'}
              icon={<DataUsage />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="متوسط التوكنز/رسالة"
              value={stats?.total_messages && client?.total_tokens_consumed ? 
                Math.round(client.total_tokens_consumed / stats.total_messages) : '0'}
              icon={<Analytics />}
              color="info"
            />
          </Grid>
        </Grid>

        {/* Success Rate - Responsive */}
        <Paper sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
          border: '1px solid',
          borderColor: 'divider',
          mb: { xs: 2, sm: 3 },
          bgcolor: 'grey.50'
        }}>
          <Typography 
            variant="h6" 
            fontWeight="600" 
            gutterBottom
            sx={{ 
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}
          >
            معدل النجاح
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            gap: { xs: 1, sm: 2 }, 
            mb: 2 
          }}>
            <Box sx={{ flexGrow: 1, width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={stats?.success_rate || 0}
                sx={{
                  height: { xs: 8, sm: 12 },
                  borderRadius: { xs: 4, sm: 6 },
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: { xs: 4, sm: 6 },
                    bgcolor: 'success.main'
                  }
                }}
              />
            </Box>
            <Typography 
              variant="h6" 
              fontWeight="700" 
              color="success.main"
              sx={{ 
                fontSize: { xs: '1rem', sm: '1.25rem' },
                minWidth: { xs: 'auto', sm: '60px' },
                textAlign: { xs: 'center', sm: 'right' }
              }}
            >
              {stats?.success_rate || 0}%
            </Typography>
          </Box>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}
          >
            نسبة المحادثات التي تم حلها بنجاح
          </Typography>
        </Paper>

        {/* Token Consumption Details */}
        <Paper sx={{
          p: 3,
          mt: 3,
          borderRadius: 3,
          boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50'
        }}>
          <Typography variant="h6" fontWeight="600" gutterBottom sx={{ 
            mb: 3
          }}>
            تفاصيل استهلاك التوكنز
          </Typography>
          
          <Grid container spacing={3}>
            {/* إجمالي استهلاك التوكنز */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'white', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.15)'
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Token color="primary" />
                  <Typography variant="h6" fontWeight="600">
                    إجمالي استهلاك التوكنز
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="700" color="primary.main">
                  {client?.total_tokens_consumed ? client.total_tokens_consumed.toLocaleString() : '0'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  توكنز
                </Typography>
              </Box>
            </Grid>

            {/* متوسط التوكنز لكل رسالة */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'white', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.15)'
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Analytics color="info" />
                  <Typography variant="h6" fontWeight="600">
                    متوسط التوكنز/رسالة
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="700" color="info.main">
                  {stats?.total_messages && client?.total_tokens_consumed ? 
                    Math.round(client.total_tokens_consumed / stats.total_messages) : '0'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  توكنز لكل رسالة
                </Typography>
              </Box>
            </Grid>

            {/* توكنز المدخلات */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'white', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.15)'
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Memory color="warning" />
                  <Typography variant="h6" fontWeight="600">
                    توكنز المدخلات
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="700" color="warning.main">
                  {client?.total_prompt_tokens ? client.total_prompt_tokens.toLocaleString() : '0'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  توكنز من المستخدم
                </Typography>
              </Box>
            </Grid>

            {/* توكنز المخرجات */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'white', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.15)'
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <DataUsage color="success" />
                  <Typography variant="h6" fontWeight="600">
                    توكنز المخرجات
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="700" color="success.main">
                  {client?.total_completion_tokens ? client.total_completion_tokens.toLocaleString() : '0'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  توكنز من البوت
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* نسبة التوكنز */}
          {client?.total_tokens_consumed && client.total_tokens_consumed > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                توزيع التوكنز
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">توكنز المدخلات</Typography>
                      <Typography variant="body2" fontWeight="600" color="warning.main">
                        {client.total_prompt_tokens ? 
                          Math.round((client.total_prompt_tokens / client.total_tokens_consumed) * 100) : 0}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={client.total_prompt_tokens ? 
                        (client.total_prompt_tokens / client.total_tokens_consumed) * 100 : 0}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          bgcolor: 'warning.main'
                        }
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">توكنز المخرجات</Typography>
                      <Typography variant="body2" fontWeight="600" color="success.main">
                        {client.total_completion_tokens ? 
                          Math.round((client.total_completion_tokens / client.total_tokens_consumed) * 100) : 0}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={client.total_completion_tokens ? 
                        (client.total_completion_tokens / client.total_tokens_consumed) * 100 : 0}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          bgcolor: 'success.main'
                        }
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>

        </Container>
      </motion.div>
    </>
  );
}


